import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Lock } from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { useBrand } from '../../brand/BrandProvider';
import { forgotPassword, resetPasswordWithCode } from '../../api/auth';

/**
 * Password reset — §7.1. Self-contained code flow: request a code by email, then
 * enter the code + a new password. Backed by POST /auth/forgot-password (no
 * account enumeration) + POST /auth/reset-password.
 */
export function ForgotPassword() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canReset = code.trim().length >= 4 && password.length >= 8 && !mismatch;

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      setStep('reset');
    } catch {
      setError('Could not start the reset. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function doReset(e: React.FormEvent) {
    e.preventDefault();
    if (!canReset) return;
    setSubmitting(true);
    setError(null);
    try {
      await resetPasswordWithCode(email.trim(), code.trim(), password);
      void navigate({ to: '/login' });
    } catch {
      setError('That code is invalid or has expired. Request a new one.');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark grid lg:grid-cols-2">
      {/* Left — pitch */}
      <div className="hidden lg:flex relative overflow-hidden p-12 xl:p-20 flex-col justify-between border-r border-zinc-200 dark:border-zinc-700">
        <div className="glow-bg absolute inset-0 pointer-events-none" aria-hidden="true" />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <Logo />
            <span className="font-semibold text-lg tracking-tight text-zinc-950 dark:text-zinc-50">{brand.name}</span>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05] max-w-lg">
            Locked out?
            <br />
            <span className="text-zinc-500 dark:text-zinc-500">We've got you.</span>
          </h1>
          <p className="mt-6 text-base text-zinc-600 dark:text-zinc-300 max-w-md">
            Enter your email and we'll send you a 4-digit reset code. It's valid for 10 minutes and works once.
          </p>
        </div>
        <div className="relative text-xs text-zinc-500 dark:text-zinc-400">© 2026 ITCity Ltd. · Sylhet, Bangladesh</div>
      </div>

      {/* Right — form */}
      <div className="relative flex flex-col justify-center p-6 sm:p-12">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
          <AuthThemeToggle />
        </div>
        <div className="lg:hidden flex items-center gap-2.5 mb-12">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">{brand.name}</span>
        </div>

        <div className="w-full max-w-sm mx-auto">
          {step === 'request' ? (
            <>
              <h2 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Reset your password</h2>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                Enter the email associated with your account. We'll send a reset code.
              </p>
              <form onSubmit={requestCode} className="mt-8 space-y-5">
                <Input label="Email" type="email" required placeholder="you@your-shop.com" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                {error && <p className="text-sm text-rose-600 dark:text-rose-400" role="alert">{error}</p>}
                <Button type="submit" fullWidth loading={submitting} iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}>
                  {submitting ? 'Sending…' : 'Send reset code'}
                </Button>
                <div className="flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400">
                  <Lock className="w-3 h-3" />
                  <span>Reset code is valid for 10 minutes</span>
                </div>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Enter your code</h2>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                We sent a code to <span className="font-medium text-zinc-900 dark:text-zinc-100">{email}</span>. Enter it and choose a new password.
              </p>
              <form onSubmit={doReset} className="mt-8 space-y-5">
                <Input label="Reset code" inputMode="numeric" required placeholder="1234" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoComplete="one-time-code" />
                <Input label="New password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" error={tooShort ? 'Use at least 8 characters.' : undefined} />
                <Input label="Confirm password" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" error={mismatch ? "Passwords don't match." : undefined} />
                {error && <p className="text-sm text-rose-600 dark:text-rose-400" role="alert">{error}</p>}
                <Button type="submit" fullWidth loading={submitting} disabled={!canReset || submitting}>
                  {submitting ? 'Resetting…' : 'Reset password'}
                </Button>
                <button type="button" onClick={() => { setStep('request'); setError(null); }} className="text-xs text-brand dark:text-brand-dm hover:underline">
                  Use a different email
                </button>
              </form>
            </>
          )}

          <p className="mt-10 text-sm text-zinc-600 dark:text-zinc-300 text-center">
            <Link to="/login" className="inline-flex items-center gap-1 font-medium text-brand dark:text-brand-dm hover:underline">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
